import React from 'react';
import { usePeersona } from '../../contexts/peersona-context';
import useAuth from '../../hooks/utils/useAuth';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { PeersonaAvatar } from './peersona-avatar';
import { PeersonaIntroVideo } from './peersona-intro-video';
import { PeersonaSparkle } from './peersona-sparkle';
import {
  PEERSONA_BUBBLE_ASSISTANT_BG,
  PEERSONA_BUBBLE_ASSISTANT_TEXT,
  PEERSONA_REFERRAL_ACCENT,
} from './peersona-styles';

interface Props {
  /** Called when the user taps the back arrow to return to the conversation. */
  onBack: () => void;
  /** Called when the user dismisses the entire widget (X in the header). */
  onClose: () => void;
}

const TraitPill: React.FC<{ label: string }> = ({ label }) => (
  <span
    className="font-open-sans rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800"
    style={{ color: PEERSONA_REFERRAL_ACCENT }}
  >
    {label}
  </span>
);

/**
 * About drawer — full-panel takeover with avatar hero, scope/trait
 * detail, a "How {name} Talks" sample bubble, and static "What to
 * Expect" copy. Trait pills render only when ``chatbot.traits`` is
 * populated by the backend.
 */
export const PeersonaAboutDrawer: React.FC<Props> = ({ onBack, onClose }) => {
  const { chatbot } = usePeersona();
  const { user } = useAuth();
  const isMdUp = useMediaQuery('md');
  const peerName = chatbot?.display_name || chatbot?.name || 'this peer';
  const callerName = (user?.first_name ?? '').trim() || 'you';
  const avatar = chatbot?.avatar_file_path ?? null;
  const scopeLine = (chatbot?.scope_line ?? '').trim();
  const welcome = (chatbot?.welcome_message ?? '').trim();
  const traits = (chatbot?.traits ?? []).map((t) => t.trim()).filter(Boolean);
  const welcomeVideoVimeoId = chatbot?.welcome_video_vimeo_id ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div
        className={
          isMdUp
            ? 'bg-white px-2.5 pt-3'
            : 'sticky top-0 z-[2] bg-white px-2.5 pt-[calc(env(safe-area-inset-top,0px)+12px)]'
        }
      >
        <header className="shrink-0 bg-white">
          <div className="flex min-h-[64px] items-center gap-1 px-1 py-1.5">
            <button
              type="button"
              aria-label="Back to conversation"
              onClick={onBack}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-700 hover:bg-gray-100"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="font-albert-sans min-w-0 flex-1 truncate text-center text-sm font-semibold text-gray-900">
              About {peerName}
            </p>
            <button
              type="button"
              aria-label="Close Peersona chat"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-600 hover:bg-gray-100"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="bg-input-border-gray h-px w-full" aria-hidden />
        </header>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-5 py-5"
        style={{ paddingBottom: 'max(20px, calc(20px + env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="flex flex-col items-center text-center">
          <PeersonaIntroVideo peerName={peerName} welcomeVideoVimeoId={welcomeVideoVimeoId} avatar={avatar} />
          <p className="font-albert-sans mt-4 text-2xl font-semibold text-gray-900">{peerName}</p>
          <p
            className="font-open-sans mt-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em]"
            style={{ color: PEERSONA_REFERRAL_ACCENT }}
          >
            <PeersonaSparkle size={12} />
            Peersona
          </p>
          {scopeLine ? (
            <p className="font-open-sans mt-3 max-w-[26rem] text-sm leading-snug text-gray-600">{scopeLine}</p>
          ) : null}
          {traits.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {traits.map((t) => (
                <TraitPill key={t} label={t} />
              ))}
            </div>
          ) : null}
        </div>

        <div className="bg-input-border-gray my-5 h-px w-full" aria-hidden />

        {welcome ? (
          <section className="rounded-2xl bg-indigo-50/40 p-4">
            <h3 className="font-albert-sans text-sm font-semibold text-gray-900">How {peerName} Talks</h3>
            {/* Mirrors the real assistant-bubble style so the preview is honest. */}
            <div className="mt-3 flex items-start gap-2">
              <PeersonaAvatar size={32} src={avatar} name={peerName} />
              <div
                className="min-w-0 rounded-bl-[4px] rounded-br-[20px] rounded-tl-[20px] rounded-tr-[20px] px-5 py-3.5"
                style={{ backgroundColor: PEERSONA_BUBBLE_ASSISTANT_BG }}
              >
                <p
                  className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
                  style={{ color: PEERSONA_BUBBLE_ASSISTANT_TEXT }}
                >
                  {welcome}
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <section className="mt-4 rounded-2xl bg-indigo-50/40 p-4">
          <h3 className="font-albert-sans text-sm font-semibold text-gray-900">What to Expect</h3>
          <ul className="font-open-sans mt-2 space-y-2 text-sm leading-6 text-gray-800">
            <li className="flex gap-2">
              <span aria-hidden style={{ color: PEERSONA_REFERRAL_ACCENT }}>
                •
              </span>
              <span>
                {peerName} only shares from {peerName}&rsquo;s lived experiences whenever it connects to a topic that
                you bring up.
              </span>
            </li>
            <li className="flex gap-2">
              <span aria-hidden style={{ color: PEERSONA_REFERRAL_ACCENT }}>
                •
              </span>
              <span>
                When {peerName} doesn&rsquo;t have a lived experience for something, you&rsquo;ll see a hand-off to
                another peer who does.
              </span>
            </li>
            <li className="flex gap-2">
              <span aria-hidden style={{ color: PEERSONA_REFERRAL_ACCENT }}>
                •
              </span>
              <span>Conversations stay here &mdash; they aren&rsquo;t shared with {callerName} or anyone else.</span>
            </li>
          </ul>
        </section>

        <div className="mt-6">
          <button
            type="button"
            onClick={onBack}
            className="font-albert-sans w-full rounded-full bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-gray-800"
          >
            Back to the conversation
          </button>
        </div>
      </div>
    </div>
  );
};
